import {
  Room, RoomRental, Teacher, Student, CourseTemplate, Group, GroupStudent,
  Session, Attendance, Invoice, Payment, Quiz, QuizAttempt,
  AcademyParameter, PlanningControl, AcademyDiscount, DiscountRequest, EmailTemplate,
  AcademySubscriptionPlan, AcademySubscription, AcademySubscriptionInvoice, SubscriptionUsage,
  TeacherAbsenceRequest
} from '../types/academy';
import { academyApi, mapRoom, mapRoomRental, mapTeacher, mapStudent, mapCourse, mapGroup, mapSession, mapQuiz, mapPlanningControl, mapDiscount, mapDiscountRequest, mapEmailTemplate, mapInvoice, mapPayment, mapSubscriptionPlan, mapSubscription, mapSubscriptionInvoice, mapTeacherAbsenceRequest } from './academyApi';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';
import { blobticRealtimeService } from '../../Commun/services/blobticRealtimeService';


export interface DocumentNumberingConfig {
  prefix: string;
  agencyLength?: number;
  dateFormat: string;
  padding: number;
  suffix: string;
  order: string;
  separator: string;
}

export function formatDocumentNumber(
  config: DocumentNumberingConfig,
  counter: number,
  date: Date = new Date(),
  agencyCode: string = ''
): string {
  const {
    prefix = '',
    agencyLength = 0,
    dateFormat = 'YYYY',
    padding = 4,
    suffix = '',
    order = 'PREFIX,AGENCY,DATE,NUMBER,SUFFIX',
    separator = '-'
  } = config;

  let formattedAgency = agencyCode ? agencyCode.trim() : '';
  if (formattedAgency && agencyLength > 0 && formattedAgency.length > agencyLength) {
    formattedAgency = formattedAgency.substring(0, agencyLength);
  }

  let formattedDate = '';
  const yyyy = date.getFullYear().toString();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = yyyy.slice(-2);

  switch (dateFormat) {
    case 'YYYY': formattedDate = yyyy; break;
    case 'YYYY-MM': formattedDate = `${yyyy}-${mm}`; break;
    case 'YYYY/MM': formattedDate = `${yyyy}/${mm}`; break;
    case 'YYYYMMDD': formattedDate = `${yyyy}${mm}${dd}`; break;
    case 'YYMM': formattedDate = `${yy}${mm}`; break;
    case 'None': case '': default: formattedDate = ''; break;
  }

  const padLen = Math.max(1, padding || 4);
  const formattedNumber = String(Math.max(1, counter || 1)).padStart(padLen, '0');

  const tokens: Record<string, string> = {
    PREFIX: prefix.trim(),
    AGENCY: formattedAgency,
    DATE: formattedDate.trim(),
    NUMBER: formattedNumber,
    SUFFIX: suffix.trim()
  };

  const orderTokens = order.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
  const sep = (separator === 'None' || separator === 'none') ? '' : (separator ?? '-');
  return orderTokens.map(token => tokens[token]).filter(val => val !== undefined && val !== '').join(sep);
}

export function shouldResetCounter(resetPeriod: string, lastResetDateStr: string, now: Date = new Date()): boolean {
  if (resetPeriod === 'Never' || resetPeriod === 'Manual') return false;
  if (!lastResetDateStr) return true;
  const lastDate = new Date(lastResetDateStr);
  if (isNaN(lastDate.getTime())) return true;
  if (resetPeriod === 'Yearly') return now.getFullYear() > lastDate.getFullYear();
  if (resetPeriod === 'Monthly') return now.getFullYear() > lastDate.getFullYear() || (now.getFullYear() === lastDate.getFullYear() && now.getMonth() > lastDate.getMonth());
  if (resetPeriod === 'Daily') return now.toISOString().split('T')[0] > lastDate.toISOString().split('T')[0];
  return false;
}


class AcademyStore {
  private listeners: (() => void)[] = [];
  private initialized: boolean = false;

  public kpiList: any[] = [];
  public kpiData: Record<string, any> = {};

  // Kept in memory (small, config-like, accessed synchronously by many helpers)
  private academyParameters: AcademyParameter[] = [];
  private planningControls: PlanningControl[] = [];
  private discounts: AcademyDiscount[] = [];

  // Mapper registry for realtime events
  private _entityMappers: Record<string, (dto: any) => any> = {};

  constructor() {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/student-pre-registration')) {
      this._registerMappers();
      // Auto-invalidate snapshots and notify React components whenever any entity changes in IndexedDB
      BlobticDataManager.subscribe((objectName: string) => {
        if (objectName === '*') {
          this.notify();
        } else {
          this.notify([objectName]);
        }
      });
    }
  }

  private _registerMappers() {
    this._entityMappers = {
      'academy_room': mapRoom,
      'academy_room_rental': mapRoomRental,
      'academy_teacher': mapTeacher,
      'academy_student': mapStudent,
      'academy_course': mapCourse,
      'academy_group': mapGroup,
      'academy_session': mapSession,
      'academy_teacher_absence_request': mapTeacherAbsenceRequest,
      'academy_quiz': mapQuiz,
      'academy_planning_control': mapPlanningControl,
      'academy_discount': mapDiscount,
      'academy_discount_request': mapDiscountRequest,
      'academy_email_template': mapEmailTemplate,
      'academy_invoice': mapInvoice,
      'academy_payment': mapPayment,
      'academy_subscription_plan': mapSubscriptionPlan,
      'academy_subscription': mapSubscription,
      'academy_subscription_invoice': mapSubscriptionInvoice,
    };
  }

  public async initFromDb(userCode?: string, dosCode?: string) {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/student-pre-registration')) return;

    let uCode = userCode;
    let dCode = dosCode;
    if (!uCode || uCode === 'default') {
      const savedUserStr = localStorage.getItem('blobtic_user');
      if (savedUserStr) {
        try {
          const uObj = JSON.parse(savedUserStr);
          uCode = uObj.code || uObj.USR_CODE || uObj.userCode || (uObj.id ? String(uObj.id) : undefined);
          dCode = dCode || uObj.dosCode || uObj.DOS_CODE || '1';
        } catch { }
      }
    }
    if (!uCode || uCode === 'default') return;

    try {
      const targetDosId = parseInt(dCode || '1', 10) || 1;
      blobticRealtimeService.connect(targetDosId);
      blobticRealtimeService.subscribe((event) => this.handleRealtimeEvent(event));
      await this.refreshDashboardStats(false);
      this.initialized = true;
    } catch (err) {
      console.error('Failed to initialize AcademyStore from DB:', err);
    }
  }

  // ─── Generic entity loader ────────────────────────────────────────────────
  /**
   * Loads an entity via its API fetcher (which automatically queries IndexedDB
   * cache first via blobticService.executeZoomRequest, or falls back to the server).
   */
  public async loadEntity<T>(
    _objName: string,
    fetcher: () => Promise<T[] | null>,
    _mapper?: (dto: any) => T
  ): Promise<T[]> {
    const data = await fetcher();
    return data || [];
  }

  // ─── Real-time events ─────────────────────────────────────────────────────
  private handleRealtimeEvent(event: { type: string; objectName: string; data: any }) {
    const { type, objectName, data } = event;
    if (!objectName || !data) return;
    const objName = objectName.toLowerCase();
    const mapper = this._entityMappers[objName];

    if (type === 'EntityCreated' || type === 'EntityUpdated') {
      const mapped = mapper ? mapper(data) : data;
      BlobticDataManager.upsertItem(objName, mapped);
    } else if (type === 'EntityDeleted') {
      BlobticDataManager.removeItem(objName, data.id || data);
    }
    if (objName in this._snap) this._invalidate(objName);
    this._notifyListeners();
  }

  // ─── Subscribe / Notify ───────────────────────────────────────────────────
  public subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => { this.listeners = this.listeners.filter(l => l !== listener); };
  }

  public notify(invalidateKeys?: string[]) {
    if (invalidateKeys) {
      invalidateKeys.forEach(k => this._invalidate(k));
    } else {
      Object.keys(this._snap).forEach(k => this._invalidate(k));
    }
    this._notifyListeners();
  }

  /** Notify listeners without invalidating caches (used by _preload, realtime events). */
  private _notifyListeners() { this.listeners.forEach(l => l()); }

  // ─── KPI ──────────────────────────────────────────────────────────────────
  public async refreshDashboardStats(forceFetch: boolean = false) {
    if (!forceFetch && BlobticDataManager.isValidContext()) {
      const cachedConfig = await BlobticDataManager.getData<any>('academy_kpi_config');
      const cachedData = await BlobticDataManager.getData<any>('academy_kpi_data');
      if (cachedConfig && cachedConfig.length > 0 && cachedData && cachedData.length > 0) {
        this.kpiList = cachedConfig[0]?.list || [];
        this.kpiData = cachedData[0]?.data || {};
        this.notify();
        return { kpiList: this.kpiList, kpiData: this.kpiData };
      }
    }
    const res = await academyApi.getAcademyKpis();
    if (res) {
      this.kpiList = res.kpiList || [];
      this.kpiData = res.kpiData || {};
      if (BlobticDataManager.isValidContext()) {
        await BlobticDataManager.setData('academy_kpi_config', [{ id: 1, list: this.kpiList }]);
        await BlobticDataManager.setData('academy_kpi_data', [{ id: 1, data: this.kpiData }]);
      }
      this.notify();
    }
    return { kpiList: this.kpiList, kpiData: this.kpiData };
  }

  public getKpiList() { return this.kpiList; }
  public getKpiData() { return this.kpiData; }

  // ─── Sync Snapshot Getters (auto-preload from IDB → server → IDB) ─────────
  // Each getter returns the in-memory snapshot (initially empty).
  // On first call it triggers a background async load; when data arrives,
  // the snapshot is populated and notify() causes React to re-render.

  private _snap: Record<string, any> = {};
  private _loading: Record<string, boolean> = {};

  private _preload<T>(key: string, fetcher: () => Promise<T[] | null>): void {
    if (this._loading[key]) return;
    this._loading[key] = true;
    this.loadEntity<T>(key, fetcher).then(items => {
      this._snap[key] = items;
      this._loading[key] = false;
      this._notifyListeners();
    }).catch(() => { this._loading[key] = false; });
  }

  public getRooms(): Room[] {
    const k = 'academy_room';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Room>(k, () => academyApi.getRooms()); }
    return this._snap[k] as Room[];
  }
  public getRoomRentals(): RoomRental[] {
    const k = 'academy_room_rental';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<RoomRental>(k, () => academyApi.getRoomRentals()); }
    return this._snap[k] as RoomRental[];
  }
  public getTeachers(): Teacher[] {
    const k = 'academy_teacher';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Teacher>(k, () => academyApi.getTeachers()); }
    return this._snap[k] as Teacher[];
  }
  public getStudents(): Student[] {
    const k = 'academy_student';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Student>(k, () => academyApi.getStudents()); }
    return this._snap[k] as Student[];
  }
  public getCourses(): CourseTemplate[] {
    const k = 'academy_course';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<CourseTemplate>(k, () => academyApi.getCourses()); }
    return this._snap[k] as CourseTemplate[];
  }
  public getGroups(): Group[] {
    const k = 'academy_group';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Group>(k, () => academyApi.getGroups()); }
    return this._snap[k] as Group[];
  }
  public getGroupStudents(): GroupStudent[] {
    const k = 'academy_group_student';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<GroupStudent>(k, () => academyApi.getGroupStudents()); }
    return this._snap[k] as GroupStudent[];
  }
  public getSessions(): Session[] {
    const k = 'academy_session';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Session>(k, () => academyApi.getSessions()); }
    return this._snap[k] as Session[];
  }
  public getAttendances(): Attendance[] {
    const k = 'academy_attendance';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Attendance>(k, () => academyApi.getAttendances()); }
    return this._snap[k] as Attendance[];
  }
  public getInvoices(): Invoice[] {
    const k = 'academy_invoice';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Invoice>(k, () => academyApi.getInvoices()); }
    return this._snap[k] as Invoice[];
  }
  public getPayments(): Payment[] {
    const k = 'academy_payment';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Payment>(k, () => academyApi.getPayments()); }
    return this._snap[k] as Payment[];
  }
  public getQuizzes(): Quiz[] {
    const k = 'academy_quiz';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<Quiz>(k, () => academyApi.getQuizzes()); }
    return this._snap[k] as Quiz[];
  }
  public getQuizAttempts(): QuizAttempt[] {
    const k = 'academy_quiz_attempt';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<QuizAttempt>(k, () => academyApi.getQuizAttempts()); }
    return this._snap[k] as QuizAttempt[];
  }
  public getDiscountRequests(): DiscountRequest[] {
    const k = 'academy_discount_request';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<DiscountRequest>(k, () => academyApi.getDiscountRequests()); }
    return this._snap[k] as DiscountRequest[];
  }

  /** Invalidate a snapshot so next getter call triggers a fresh async load. */
  private _invalidate(key: string) { delete this._snap[key]; }

  /** Eagerly refresh a snapshot after mutation (returns fresh data). */
  public async _refresh<T>(key: string, fetcher: () => Promise<T[] | null>): Promise<T[]> {
    this._invalidate(key);
    const items = await this.loadEntity<T>(key, fetcher);
    this._snap[key] = items;
    this._notifyListeners();
    return items;
  }

  // ─── Sync Getters (in-memory: config entities) ────────────────────────────
  public getPlanningControls(): PlanningControl[] {
    const k = 'academy_planning_control';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<PlanningControl>(k, () => academyApi.getPlanningControls()); }
    return this._snap[k] as PlanningControl[];
  }
  public getDiscounts(): AcademyDiscount[] {
    const k = 'academy_discount';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<AcademyDiscount>(k, () => academyApi.getDiscounts()); }
    return this._snap[k] as AcademyDiscount[];
  }
  public getAcademyParameters(dosIdf?: number) {
    if (dosIdf && dosIdf > 0) return this.academyParameters.filter(p => p.dos_idf === dosIdf);
    return this.academyParameters;
  }

  public getAcademyParameterValue(code: string, dosIdf?: number, defaultValue: string = ''): string {
    const paramsForAgency = dosIdf && dosIdf > 0 ? this.academyParameters.filter(p => p.dos_idf === dosIdf) : this.academyParameters;
    const param = paramsForAgency.find(p => p.code === code) || this.academyParameters.find(p => p.code === code);
    return param ? param.value : defaultValue;
  }

  public getAgencyDashboardRefreshTimer(dosIdf?: number): number {
    const val = parseInt(this.getAcademyParameterValue('Dashboard_Refresh_Timer_Seconds', dosIdf, '60'), 10);
    return isNaN(val) ? 60 : Math.max(0, val);
  }

  public getDeleteGroupCascadeSessions(dosIdf?: number): boolean {
    const val = this.getAcademyParameterValue('Delete_Group_Cascade_Sessions', dosIdf, '1');
    return val === '1' || val.toLowerCase() === 'true';
  }

  public getPlanningStartTime(dosIdf?: number): string { return this.getAcademyParameterValue('Planning_Start_Time', dosIdf, '08:00'); }
  public getPlanningEndTime(dosIdf?: number): string { return this.getAcademyParameterValue('Planning_End_Time', dosIdf, '17:30'); }
  public getPlanningSlotDurationMinutes(dosIdf?: number): number {
    const val = parseInt(this.getAcademyParameterValue('Planning_Slot_Duration_Minutes', dosIdf, '90'), 10);
    return isNaN(val) || val <= 0 ? 90 : val;
  }
  public getPlanningFirstDayOfWeek(dosIdf?: number): number {
    const val = parseInt(this.getAcademyParameterValue('Planning_First_Day_Of_Week', dosIdf, '0'), 10);
    return isNaN(val) ? 0 : val;
  }
  public getPlanningDefaultView(dosIdf?: number): 'day' | 'week' | 'month' {
    const val = this.getAcademyParameterValue('Planning_Default_View', dosIdf, 'week');
    return (val === 'day' || val === 'week' || val === 'month') ? val : 'week';
  }
  public getPlanningWeekendDays(dosIdf?: number): number[] {
    return this.getAcademyParameterValue('Planning_Weekend_Days', dosIdf, '5,6').split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  }
  public getPlanningWeekendBgColor(dosIdf?: number): string { return this.getAcademyParameterValue('Planning_Weekend_Bg_Color', dosIdf, '#fff7ed'); }
  public getShowUserOnEditions(dosIdf?: number): boolean { return this.getAcademyParameterValue('Show_User_On_Editions', dosIdf, '1') === '1'; }

  // ─── Derived Sync Helpers ────────────────────────────────────────────────
  public getRoomUtilizationPercentage(roomId: number): number {
    const sessions = this.getSessions();
    const now = new Date();
    const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 7);
    const weekSessions = sessions.filter(s => s.room_id === roomId && new Date(s.date) >= startOfWeek && new Date(s.date) < endOfWeek);
    if (weekSessions.length === 0) return 0;
    const totalMinutes = weekSessions.reduce((sum, s) => sum + (s.duration || 120), 0);
    const maxMinutes = 5 * 8 * 60;
    return Math.min(100, Math.round((totalMinutes / maxMinutes) * 100));
  }

  public getTotalOutstanding(): number {
    return this.getInvoices().reduce((sum, inv) => sum + Math.max(0, inv.total_amount - inv.amount_paid), 0);
  }

  public getMonthlyRevenue(): number {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    return this.getPayments()
      .filter(p => p.payment_date >= monthStart && p.payment_date <= monthEnd)
      .reduce((sum, p) => sum + p.amount, 0);
  }

  // ─── Legacy ensure*Loaded (no-ops: snapshot getters auto-preload) ─────────
  public ensureRoomsLoaded() { this.getRooms(); }
  public ensureRoomRentalsLoaded() { this.getRoomRentals(); }
  public ensureTeachersLoaded() { this.getTeachers(); }
  public ensureStudentsLoaded() { this.getStudents(); }
  public ensureCoursesLoaded() { this.getCourses(); }
  public ensureGroupsLoaded() { this.getGroups(); }
  public ensureSessionsLoaded() { this.getSessions(); }
  public ensureGroupStudentsLoaded() { this.getGroupStudents(); }

  // ─── Room Mutations ───────────────────────────────────────────────────────
  public async addRoom(room: Omit<Room, 'id'>): Promise<Room> {
    const res = await academyApi.createRoom(room);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = (item as any)?.ARM_IDF ? mapRoom(item) : { ...room, id: Number((item as any)?.ARM_IDF || (item as any)?.id || res.data) } as Room;
      await BlobticDataManager.upsertItem('academy_room', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create room.');
  }

  public async updateRoom(room: Room): Promise<void> {
    const res = await academyApi.updateRoom(room.id, room);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).ARM_IDF ? mapRoom(res.data) : room;
      await BlobticDataManager.upsertItem('academy_room', item);
      this.notify();
    }
  }

  public async archiveRoom(roomId: number): Promise<{ success: boolean; message?: string }> {
    const sessions = await this.getSessions();
    const hasHistory = sessions.some(s => s.room_id === roomId);
    if (hasHistory) {
      const res = await academyApi.updateRoom(roomId, { status: 'Archived' });
      if (res && res.success) {
        await BlobticDataManager.upsertItem('academy_room', { id: roomId, status: 'Archived' } as any);
        this.notify();
        return { success: true };
      }
      return { success: false, message: (res as any)?.message || 'Failed to archive room.' };
    }
    const res = await academyApi.deleteRoom(roomId);
    if (res && res.success) {
      await BlobticDataManager.removeItem('academy_room', roomId);
      this.notify();
      return { success: true };
    }
    return { success: false, message: (res as any)?.message || 'Failed to delete room.' };
  }

  public async deleteRoom(roomId: number): Promise<{ success: boolean; message?: string }> { return this.archiveRoom(roomId); }

  // ─── Room Rental Mutations ───────────────────────────────────────────────
  public async addRoomRental(rental: Omit<RoomRental, 'id'>): Promise<RoomRental> {
    const res = await academyApi.createRoomRental(rental);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = (item as any)?.RNT_IDF ? mapRoomRental(item) : { ...rental, id: Number((item as any)?.RNT_IDF || (item as any)?.id || res.data) } as RoomRental;
      await BlobticDataManager.upsertItem('academy_room_rental', mapped);
      this.notify();
      return mapped;
    }
    throw new Error((res as any)?.message || 'Failed to create room rental.');
  }

  public async updateRoomRental(rental: RoomRental): Promise<void> {
    const res = await academyApi.updateRoomRental(rental.id, rental);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).RNT_IDF ? mapRoomRental(res.data) : rental;
      await BlobticDataManager.upsertItem('academy_room_rental', item);
      this.notify();
    }
  }

  public async deleteRoomRental(rentalId: number): Promise<{ success: boolean; message?: string }> {
    const res = await academyApi.deleteRoomRental(rentalId);
    if (res && res.success) {
      await BlobticDataManager.removeItem('academy_room_rental', rentalId);
      this.notify();
      return { success: true };
    }
    return { success: false, message: (res as any)?.message || 'Failed to delete room rental.' };
  }

  // ─── Teacher Mutations ────────────────────────────────────────────────────
  public async addTeacher(teacher: Omit<Teacher, 'id'>): Promise<Teacher> {
    const res = await academyApi.createTeacher(teacher);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = item?.TCH_IDF ? mapTeacher(item) : { ...teacher, id: Number(item?.id || res.data) } as Teacher;
      await BlobticDataManager.upsertItem('academy_teacher', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create teacher.');
  }

  public async updateTeacher(teacher: Teacher): Promise<void> {
    const res = await academyApi.updateTeacher(teacher.id, teacher);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).TCH_IDF ? mapTeacher(res.data) : teacher;
      await BlobticDataManager.upsertItem('academy_teacher', item);
      this.notify();
    }
  }

  public async deleteTeacher(teacherId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deleteTeacher(teacherId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete teacher.' };
      await BlobticDataManager.removeItem('academy_teacher', teacherId);
      this.notify();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete teacher.' };
    }
  }

  // ─── Course Mutations ─────────────────────────────────────────────────────
  public async addCourse(course: Omit<CourseTemplate, 'id'>): Promise<CourseTemplate> {
    const res = await academyApi.createCourse(course);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = item?.CRS_IDF ? mapCourse(item) : { ...course, id: Number(item?.id || res.data) } as CourseTemplate;
      await BlobticDataManager.upsertItem('academy_course', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create course.');
  }

  public async updateCourse(course: CourseTemplate): Promise<void> {
    const res = await academyApi.updateCourse(course.id, course);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).CRS_IDF ? mapCourse(res.data) : course;
      await BlobticDataManager.upsertItem('academy_course', item);
      this.notify();
    }
  }

  public async deleteCourse(courseId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deleteCourse(courseId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete course.' };
      await BlobticDataManager.removeItem('academy_course', courseId);
      this.notify();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete course.' };
    }
  }

  // ─── Student Mutations ────────────────────────────────────────────────────
  public async addStudent(student: Omit<Student, 'id'>): Promise<Student> {
    const res = await academyApi.createStudent(student);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = item?.STD_IDF ? mapStudent(item) : { ...student, id: Number(item?.id || res.data) } as Student;
      await BlobticDataManager.upsertItem('academy_student', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create student.');
  }

  public async updateStudent(student: Student): Promise<void> {
    const res = await academyApi.updateStudent(student.id, student);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).STD_IDF ? mapStudent(res.data) : student;
      await BlobticDataManager.upsertItem('academy_student', item);
      this.notify();
    }
  }

  public async deleteStudent(studentId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deleteStudent(studentId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete student.' };
      await BlobticDataManager.removeItem('academy_student', studentId);
      this.notify();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete student.' };
    }
  }

  // ─── Group Mutations ──────────────────────────────────────────────────────
  public async addGroup(group: Omit<Group, 'id'>): Promise<Group> {
    const res = await academyApi.createGroup(group);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = item?.GRP_IDF ? mapGroup(item) : { ...group, id: Number(item?.id || res.data) } as Group;
      await BlobticDataManager.upsertItem('academy_group', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create group.');
  }

  public async updateGroup(group: Group): Promise<void> {
    const res = await academyApi.updateGroup(group.id, group);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).GRP_IDF ? mapGroup(res.data) : group;
      await BlobticDataManager.upsertItem('academy_group', item);
      this.notify();
    }
  }

  public async deleteGroup(groupId: number, dosIdf?: number): Promise<{ success: boolean; message?: string }> {
    const sessions = await this.getSessions();
    const groupSessions = sessions.filter(s => s.group_id === groupId);
    const isCascade = this.getDeleteGroupCascadeSessions(dosIdf);

    if (groupSessions.length > 0 && !isCascade) {
      return { success: false, message: 'Cannot delete group: Scheduled planning sessions exist for this group and cascade deletion is disabled.' };
    }

    try {
      if (groupSessions.length > 0 && isCascade) {
        for (const s of groupSessions) { await academyApi.deleteSession(s.id); }
      }
      await academyApi.deleteGroup(groupId);
      await BlobticDataManager.removeItem('academy_group', groupId);
      this.notify();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete group.' };
    }
  }

  public async updateGroupStatus(groupId: number, status: import('../types/academy').GroupStatus): Promise<{ success: boolean; message?: string }> {
    if (status === 'Completed') {
      const sessions = await this.getSessions();
      const groupSessions = sessions.filter(s => s.group_id === groupId);
      if (groupSessions.some(s => !s.attendance_taken)) {
        return { success: false, message: 'Cannot mark Group as Completed. Attendance has not been taken for all scheduled sessions.' };
      }
    }
    const groups = await this.getGroups();
    const group = groups.find(g => g.id === groupId);
    if (!group) return { success: false, message: 'Group not found.' };

    const updatedGroup = { ...group, status };
    await academyApi.updateGroup(groupId, updatedGroup);
    await BlobticDataManager.upsertItem('academy_group', updatedGroup);
    this.notify();
    return { success: true };
  }

  // ─── Enrollment ───────────────────────────────────────────────────────────
  public async enrollStudentInGroup(groupId: number, studentId: number, manualDiscountId?: number): Promise<{ success: boolean; message?: string }> {
    const groupStudents = this.getGroupStudents();
    if (groupStudents.some(gs => gs.group_id === groupId && gs.student_id === studentId)) {
      return { success: false, message: 'Student already enrolled in this group.' };
    }

    const group = this.getGroups().find(g => g.id === groupId);
    if (!group) return { success: false, message: 'Group not found.' };

    const student = this.getStudents().find(s => s.id === studentId);
    const discountId = (manualDiscountId && manualDiscountId > 0)
      ? manualDiscountId
      : (student?.discount_id && student.discount_id > 0 ? student.discount_id : undefined);

    try {
      const enrollRes = await academyApi.enrollStudent(groupId, studentId, discountId);
      if (!enrollRes.success) return { success: false, message: enrollRes.message || 'Failed to enroll student.' };

      await Promise.all([
        this._refresh('academy_group_student', () => academyApi.getGroupStudents(false)),
        this._refresh('academy_invoice', () => academyApi.getInvoices(false)),
        this.refreshAcademyParameters(group.dos_idf),
      ]);
      await this.recalculateFinancialHolds();
      return { success: true };
    } catch (err) {
      console.error('enrollStudentInGroup failed:', err);
      return { success: false, message: 'Failed to enroll student.' };
    }
  }

  public async enrollStudentToGroup(groupId: number, studentId: number): Promise<{ success: boolean; message?: string }> {
    return this.enrollStudentInGroup(groupId, studentId);
  }

  public async unenrollStudentFromGroup(groupId: number, studentId: number): Promise<{ success: boolean; message?: string }> {
    const gs = this.getGroupStudents().find(g => g.group_id === groupId && g.student_id === studentId);
    if (!gs) return { success: true };

    try {
      const res = await academyApi.unenrollStudent(gs.id);
      if (!res.success) {
        const code = String(res.message || (res as any).code || '');
        if (code.includes('ERR_INVOICE_HAS_PAYMENTS') || code.includes('UNENROLL_HAS_PAYMENTS')) {
          return { success: false, message: 'UNENROLL_HAS_PAYMENTS' };
        }
        return { success: false, message: res.message || 'Failed to unenroll student.' };
      }

      await Promise.all([
        this._refresh('academy_group_student', () => academyApi.getGroupStudents(false)),
        this._refresh('academy_invoice', () => academyApi.getInvoices(false)),
      ]);
      await this.recalculateFinancialHolds();
      return { success: true };
    } catch (err) {
      console.error('unenrollStudentFromGroup failed:', err);
      return { success: false, message: 'Failed to unenroll student.' };
    }
  }

  // ─── Session Mutations ────────────────────────────────────────────────────
  public async addSession(session: Omit<Session, 'id'>): Promise<{ success: boolean; message?: string; session?: Session }> {
    try {
      const res = await academyApi.createSession(session);
      if (!res.success) return { success: false, message: res.message || 'Failed to schedule session.' };

      const newSsnId = Number((res.data as any)?.SSN_IDF || (res.data as any)?.id || Date.now());
      const sessionDate = new Date(session.date!);
      const dayOfWeek = isNaN(sessionDate.getTime()) ? 0 : sessionDate.getDay();
      const startIso = session.start_hour || `${session.date}T${session.start_time}:00`;
      const endIso = session.end_hour || `${session.date}T${session.end_time}:00`;

      const createdSession: Session = {
        id: newSsnId, group_id: session.group_id!, teacher_id: session.teacher_id!, room_id: session.room_id!,
        day_of_week: session.day_of_week ?? dayOfWeek, start_hour: startIso, end_hour: endIso,
        duration: session.duration && session.duration > 0 ? session.duration
          : Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000)) || 120,
        date: session.date!, start_time: session.start_time!, end_time: session.end_time!, attendance_taken: false
      };

      await BlobticDataManager.upsertItem('academy_session', createdSession);
      this.notify();
      return { success: true, session: createdSession };
    } catch (err) {
      console.error('addSession failed:', err);
      return { success: false, message: 'Failed to schedule session.' };
    }
  }

  public async deleteSession(sessionId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deleteSession(sessionId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete session.' };
      await BlobticDataManager.removeItem('academy_session', sessionId);
      this.notify();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete session.' };
    }
  }

  public async updateSession(sessionId: number, changes: Partial<Session>): Promise<{ success: boolean; message?: string; session?: Session }> {
    try {
      const sessions = await this.getSessions();
      const existing = sessions.find(s => s.id === sessionId);
      const merged: Partial<Session> = {
        group_id: changes.group_id ?? existing?.group_id,
        room_id: changes.room_id ?? existing?.room_id,
        teacher_id: changes.teacher_id ?? existing?.teacher_id,
        day_of_week: changes.day_of_week,
        start_hour: changes.start_hour || (changes.date || existing?.date
          ? `${(changes.date || existing?.date)!.slice(0, 10)}T${(changes.start_time || existing?.start_time || '09:00')}:00` : undefined),
        end_hour: changes.end_hour || (changes.date || existing?.date
          ? `${(changes.date || existing?.date)!.slice(0, 10)}T${(changes.end_time || existing?.end_time || '11:00')}:00` : undefined),
        duration: changes.duration,
        is_recurring: changes.is_recurring ?? existing?.is_recurring,
        status: changes.status ?? existing?.status,
        status_reason: changes.status_reason ?? existing?.status_reason,
        postponed_date: changes.postponed_date ?? existing?.postponed_date,
        postponed_time: changes.postponed_time ?? existing?.postponed_time,
        teacher_absent: changes.teacher_absent ?? existing?.teacher_absent
      };

      const res = await academyApi.updateSession(sessionId, merged);
      if (!res.success) return { success: false, message: res.message || 'Failed to update session.' };

      if (existing) {
        const newStartIso = merged.start_hour!;
        const newEndIso = merged.end_hour!;
        const startDate = new Date(newStartIso);
        const endDate = new Date(newEndIso);
        const fmt = (n: number) => String(n).padStart(2, '0');
        const updated: Session = {
          ...existing,
          group_id: merged.group_id ?? existing.group_id,
          room_id: merged.room_id ?? existing.room_id,
          teacher_id: merged.teacher_id ?? existing.teacher_id,
          ...(Number.isNaN(startDate.getTime()) ? {} : {
            day_of_week: changes.day_of_week ?? startDate.getDay(),
            start_hour: newStartIso,
            date: `${startDate.getFullYear()}-${fmt(startDate.getMonth() + 1)}-${fmt(startDate.getDate())}`,
            start_time: `${fmt(startDate.getHours())}:${fmt(startDate.getMinutes())}`,
          }),
          ...(Number.isNaN(endDate.getTime()) ? {} : {
            end_hour: newEndIso,
            end_time: `${fmt(endDate.getHours())}:${fmt(endDate.getMinutes())}`,
          }),
          duration: changes.duration && changes.duration > 0 ? changes.duration
            : Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000)) || existing.duration,
          status: merged.status ?? existing.status,
          status_reason: merged.status_reason ?? existing.status_reason,
          postponed_date: merged.postponed_date ?? existing.postponed_date,
          postponed_time: merged.postponed_time ?? existing.postponed_time,
          teacher_absent: merged.teacher_absent ?? existing.teacher_absent
        };
        await BlobticDataManager.upsertItem('academy_session', updated);
        this.notify();
        return { success: true, session: updated };
      }
      this.notify();
      return { success: true };
    } catch (err) {
      console.error('updateSession failed:', err);
      return { success: false, message: 'Failed to update session.' };
    }
  }

  // ─── Teacher Absence Requests ─────────────────────────────────────────────
  public getTeacherAbsenceRequests(): TeacherAbsenceRequest[] {
    const k = 'academy_teacher_absence_request';
    if (!(k in this._snap)) { this._snap[k] = []; this._preload<TeacherAbsenceRequest>(k, () => academyApi.getTeacherAbsenceRequests()); }
    return this._snap[k] as TeacherAbsenceRequest[];
  }

  public async ensureTeacherAbsenceRequestsLoaded(): Promise<TeacherAbsenceRequest[]> {
    const k = 'academy_teacher_absence_request';
    const items = await this.loadEntity<TeacherAbsenceRequest>(k, () => academyApi.getTeacherAbsenceRequests());
    this._snap[k] = items;
    this._notifyListeners();
    return items;
  }

  public async validateTeacherAbsenceRequest(
    tarId: number,
    status: 'APPROVED' | 'REJECTED',
    adminComment: string,
    options?: { updateSession?: boolean; sessionStatus?: 'Cancelled' | 'Postponed'; postponedDate?: string; postponedTime?: string }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.updateTeacherAbsenceRequest(tarId, {
        status,
        admin_comment: adminComment,
        decision_date: new Date().toISOString()
      });
      if (!res.success) return { success: false, message: res.message || 'Failed to update request' };

      // Update local request store
      const requests = this.getTeacherAbsenceRequests();
      const existing = requests.find(r => r.id === tarId);
      if (existing) {
        const updatedReq: TeacherAbsenceRequest = {
          ...existing,
          status,
          admin_comment: adminComment,
          decision_date: new Date().toISOString()
        };
        await BlobticDataManager.upsertItem('academy_teacher_absence_request', updatedReq);
        this._snap['academy_teacher_absence_request'] = requests.map(r => r.id === tarId ? updatedReq : r);
      }

      // If linked to a session and updateSession is requested
      if (existing?.ssn_idf && options?.updateSession) {
        const ssnStatus = options.sessionStatus || 'Cancelled';
        const reason = adminComment ? `Absence validée: ${adminComment}` : 'Absence enseignant validée';
        await this.updateSession(existing.ssn_idf, {
          status: ssnStatus,
          status_reason: reason,
          teacher_absent: true,
          postponed_date: options.postponedDate,
          postponed_time: options.postponedTime
        });
      }

      this._notifyListeners();
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err.message || 'Error validating request' };
    }
  }

  // ─── Attendance ───────────────────────────────────────────────────────────
  public async markAttendance(sessionId: number, studentId: number, status: import('../types/academy').AttendanceStatus): Promise<{ success: boolean; message?: string }> {
    const students = await this.getStudents();
    const student = students.find(s => s.id === studentId);
    if (student?.financial_hold && (status === 'Present' || status === 'Late')) {
      return { success: false, message: `Financial Hold Block: ${student.first_name} ${student.last_name} is currently flagged for unpaid debt.` };
    }

    const attendances = await this.getAttendances();
    const existing = attendances.find(a => a.session_id === sessionId && a.student_id === studentId);
    const checkInTime = status === 'Present' || status === 'Late' ? new Date().toISOString() : undefined;

    try {
      const apiRes = existing
        ? await academyApi.updateAttendance(existing.id, { status, check_in_time: checkInTime })
        : await academyApi.createAttendance({ session_id: sessionId, student_id: studentId, status, check_in_time: checkInTime });

      if (apiRes.success) {
        const atdId = existing?.id || Number((apiRes.data as any)?.ATD_IDF || (apiRes.data as any)?.id || Date.now());
        await BlobticDataManager.upsertItem('academy_attendance', {
          id: atdId, session_id: sessionId, student_id: studentId, status, check_in_time: checkInTime
        } as Attendance);

        const sessions = await this.getSessions();
        const sessIdx = sessions.findIndex(s => s.id === sessionId);
        if (sessIdx !== -1) {
          await BlobticDataManager.upsertItem('academy_session', { ...sessions[sessIdx], attendance_taken: true });
        }
        this.notify();
        return { success: true };
      }
      return { success: false, message: apiRes.message || 'Failed to mark attendance.' };
    } catch (err) {
      console.error('markAttendance failed:', err);
      return { success: false, message: 'Failed to mark attendance.' };
    }
  }

  public async saveAttendanceJustification(
    sessionId: number,
    studentId: number,
    reason: string,
    docName?: string,
    docBase64?: string
  ): Promise<{ success: boolean; message?: string }> {
    const attendances = await this.getAttendances();
    const existing = attendances.find(a => a.session_id === sessionId && a.student_id === studentId);
    const nowIso = new Date().toISOString();

    try {
      const apiRes = existing
        ? await academyApi.updateAttendance(existing.id, {
            justified: true,
            justification_reason: reason,
            justification_doc_name: docName || '',
            justification_doc_base64: docBase64 || '',
            justification_date: nowIso
          })
        : await academyApi.createAttendance({
            session_id: sessionId,
            student_id: studentId,
            status: 'Absent',
            justified: true,
            justification_reason: reason,
            justification_doc_name: docName || '',
            justification_doc_base64: docBase64 || '',
            justification_date: nowIso
          });

      if (apiRes.success) {
        const atdId = existing?.id || Number((apiRes.data as any)?.ATD_IDF || (apiRes.data as any)?.id || Date.now());
        const updatedItem: Attendance = {
          ...(existing || { id: atdId, session_id: sessionId, student_id: studentId, status: 'Absent' }),
          id: atdId,
          justified: true,
          justification_reason: reason,
          justification_doc_name: docName || '',
          justification_doc_base64: docBase64 || '',
          justification_date: nowIso
        };
        await BlobticDataManager.upsertItem('academy_attendance', updatedItem);
        this.notify();
        return { success: true };
      }
      return { success: false, message: apiRes.message || 'Failed to save justification.' };
    } catch (err: any) {
      console.error('saveAttendanceJustification failed:', err);
      return { success: false, message: err?.message || 'Failed to save justification.' };
    }
  }

  // ─── Financial ────────────────────────────────────────────────────────────
  public async recordPayment(studentId: number, amount: number, method: import('../types/academy').PaymentMethod): Promise<{ success: boolean; message?: string; overpayment?: number }> {
    try {
      const res = await academyApi.recordStudentPayment(studentId, amount, method);
      if (!res.success) {
        const code = String(res.message || (res as any).code || '');
        if (code.includes('ERR_NO_UNPAID') || code.toLowerCase().includes('no outstanding')) {
          return { success: false, message: 'Student has no outstanding unpaid invoices.' };
        }
        return { success: false, message: res.message || 'Failed to record payment.' };
      }

      await Promise.all([
        this._refresh('academy_invoice', () => academyApi.getInvoices(false)),
        this._refresh('academy_payment', () => academyApi.getPayments(false)),
        this.refreshAcademyParameters(),
      ]);
      await this.recalculateFinancialHolds();

      const data = (res.data || {}) as Record<string, any>;
      const overpayment = Number(data.overpayment ?? data.Overpayment ?? 0);
      return overpayment > 0
        ? { success: true, message: 'overpayment', overpayment }
        : { success: true, message: 'allocated' };
    } catch (err) {
      console.error('recordPayment failed:', err);
      return { success: false, message: 'Failed to record payment.' };
    }
  }

  public async deletePayment(paymentId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deletePayment(paymentId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete payment.' };

      await Promise.all([
        this._refresh('academy_invoice', () => academyApi.getInvoices(false)),
        this._refresh('academy_payment', () => academyApi.getPayments(false)),
      ]);
      await this.recalculateFinancialHolds();
      return { success: true };
    } catch (err) {
      console.error('deletePayment failed:', err);
      return { success: false, message: 'Failed to delete payment.' };
    }
  }

  public async deleteInvoice(invoiceId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deleteInvoice(invoiceId);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete invoice.' };
      await BlobticDataManager.removeItem('academy_invoice', invoiceId);
      await this.recalculateFinancialHolds();
      this.notify();
      return { success: true };
    } catch (err) {
      console.error('deleteInvoice failed:', err);
      return { success: false, message: 'Failed to delete invoice.' };
    }
  }

  // ─── Direct Invoice Discount Mutation (Admin, "Planificateur", or "Auto" role) ───
  public async applyInvoiceDiscountDirect(invoiceId: number, discountData: {
    discount_id?: number;
    discount_code?: string;
    discount_percent: number;
    discount_amount: number;
    original_amount: number;
    new_amount: number;
    justification: string;
    user_lib?: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const invoices = this.getInvoices();
      const inv = invoices.find(i => i.id === invoiceId);
      if (!inv) return { success: false, message: 'Facture introuvable.' };

      const newStatus: Invoice['status'] = inv.amount_paid >= discountData.new_amount ? 'Paid' : (inv.amount_paid > 0 ? 'Partial' : 'Unpaid');

      const updatedInv: Partial<Invoice> = {
        total_amount: discountData.new_amount,
        original_amount: discountData.original_amount,
        discount_id: discountData.discount_id,
        discount_code: discountData.discount_code,
        discount_percent: discountData.discount_percent,
        discount_amount: discountData.discount_amount,
        discount_reason: discountData.justification,
        discount_approved: true,
        discount_approved_by: discountData.user_lib || 'Admin',
        status: newStatus
      };

      const res = await academyApi.updateInvoice(invoiceId, updatedInv);
      if (res.success) {
        await BlobticDataManager.upsertItem('academy_invoice', { ...inv, ...updatedInv });
        await this._refresh('academy_invoice', () => academyApi.getInvoices());
        this.notify(['academy_invoice']);
        return { success: true };
      }
      return { success: false, message: res.message || 'Échec de mise à jour de la facture.' };
    } catch (err: any) {
      console.error('applyInvoiceDiscountDirect error:', err);
      return { success: false, message: err.message || 'Erreur lors de l\'application de la remise.' };
    }
  }

  // ─── Submit Discount Request (Pending validation by Admin) ─────────────────
  public async submitDiscountRequest(reqData: Partial<DiscountRequest>): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.createDiscountRequest(reqData);
      if (res.success) {
        await this._refresh('academy_discount_request', () => academyApi.getDiscountRequests());
        this.notify(['academy_discount_request']);
        return { success: true };
      }
      return { success: false, message: res.message || 'Échec de soumission de la demande de remise.' };
    } catch (err: any) {
      console.error('submitDiscountRequest error:', err);
      return { success: false, message: err.message || 'Erreur lors de la soumission de la demande.' };
    }
  }

  // ─── Approve Discount Request (Admin Action) ──────────────────────────────
  public async approveDiscountRequest(requestId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.approveDiscountRequest(requestId);
      if (res.success) {
        if (res.data) {
          const mapped = mapDiscountRequest(res.data);
          await BlobticDataManager.upsertItem('academy_discount_request', mapped);
        }
        await this._refresh('academy_discount_request', () => academyApi.getDiscountRequests(false));
        await this._refresh('academy_invoice', () => academyApi.getInvoices(false));
        this.notify(['academy_discount_request', 'academy_invoice']);
        return { success: true };
      }
      return { success: false, message: res.message || 'Échec de validation de la demande.' };
    } catch (err: any) {
      console.error('approveDiscountRequest error:', err);
      return { success: false, message: err.message || 'Erreur lors de la validation de la demande.' };
    }
  }

  // ─── Reject Discount Request (Admin Action) ───────────────────────────────
  public async rejectDiscountRequest(requestId: number, comment?: string): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.rejectDiscountRequest(requestId, comment);
      if (res.success) {
        if (res.data) {
          const mapped = mapDiscountRequest(res.data);
          await BlobticDataManager.upsertItem('academy_discount_request', mapped);
        }
        await this._refresh('academy_discount_request', () => academyApi.getDiscountRequests(false));
        this.notify(['academy_discount_request']);
        return { success: true };
      }
      return { success: false, message: res.message || 'Échec du refus de la demande.' };
    } catch (err: any) {
      console.error('rejectDiscountRequest error:', err);
      return { success: false, message: err.message || 'Erreur lors du refus de la demande.' };
    }
  }

  // ─── Financial Hold Business Rule ─────────────────────────────────────────
  public async recalculateFinancialHolds() {
    const students = await this.getStudents();
    const invoices = await this.getInvoices();
    const today = new Date();

    for (const student of students) {
      const studentInvoices = invoices.filter(i => i.student_id === student.id);
      const totalAmount = studentInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
      if (totalAmount === 0) { student.financial_hold = false; continue; }

      const totalPaid = studentInvoices.reduce((sum, inv) => sum + inv.amount_paid, 0);
      const debtPercentage = ((totalAmount - totalPaid) / totalAmount) * 100;
      const oldestOverdue = studentInvoices.find(inv => {
        if (inv.status === 'Paid') return false;
        return Math.floor((today.getTime() - new Date(inv.due_date).getTime()) / 86400000) > 15;
      });

      student.financial_hold = debtPercentage > 30 && Boolean(oldestOverdue);
      if (student.financial_hold) student.hold_since = oldestOverdue!.due_date;
      await BlobticDataManager.upsertItem('academy_student', student);
    }
  }

  // ─── Discount ─────────────────────────────────────────────────────────────
  public async isDiscountUsed(discountId: number): Promise<boolean> {
    const invoices = await this.getInvoices();
    const students = await this.getStudents();
    const disc = this.discounts.find(d => d.id === discountId);
    if (!disc) return false;
    return invoices.some(inv => inv.discount_id === discountId || inv.discount_code === disc.code)
      || students.some(s => s.discount_id === discountId || s.discount_code === disc.code);
  }

  public async computeBestDiscountForStudent(studentId: number, basePrice: number, manualDiscountId?: number): Promise<{
    discountId?: number; discountCode?: string; discountPercent: number;
    discountAmount: number; finalPrice: number; requiresValidation: boolean; reason?: string;
  }> {
    const students = await this.getStudents();
    const groupStudents = await this.getGroupStudents();
    const student = students.find(s => s.id === studentId);
    let selectedDisc: AcademyDiscount | undefined;

    if (manualDiscountId) {
      selectedDisc = this.discounts.find(d => d.id === manualDiscountId && d.is_active);
    } else if (student?.discount_id) {
      selectedDisc = this.discounts.find(d => d.id === student.discount_id && d.is_active);
    }

    if (!selectedDisc) {
      const enrolledCount = groupStudents.filter(gs => gs.student_id === studentId).length;
      if (enrolledCount >= 1) {
        selectedDisc = this.discounts.find(d => d.code === 'PACK' && d.is_active)
          || this.discounts.find(d => d.code === 'FID' && d.is_active);
      }
    }

    if (!selectedDisc) return { discountPercent: 0, discountAmount: 0, finalPrice: basePrice, requiresValidation: false };

    const discountAmount = Math.round((basePrice * selectedDisc.percentage) / 100);
    return {
      discountId: selectedDisc.id, discountCode: selectedDisc.code, discountPercent: selectedDisc.percentage,
      discountAmount, finalPrice: Math.max(0, basePrice - discountAmount),
      requiresValidation: selectedDisc.requires_validation, reason: selectedDisc.description || selectedDisc.name
    };
  }

  // ─── Planning Controls ────────────────────────────────────────────────────
  public async savePlanningControl(ctrl: Partial<PlanningControl>) {
    const res = await academyApi.savePlanningControl(ctrl);
    if (res && res.success) {
      this.notify(['academy_planning_control']);
    }
    return res;
  }

  public async deletePlanningControl(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await academyApi.deletePlanningControl(id);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete planning control.' };
      await BlobticDataManager.removeItem('academy_planning_control', id);
      this.notify(['academy_planning_control']);
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete planning control.' };
    }
  }

  // ─── Discount Management ──────────────────────────────────────────────────
  public async saveDiscount(discount: Partial<AcademyDiscount>) {
    const isUpdate = Boolean(discount.id);
    const res = isUpdate ? await academyApi.updateDiscount(discount.id!, discount) : await academyApi.createDiscount(discount);
    if (res && res.success) {
      this.notify(['academy_discount']);
    }
    return res;
  }

  public async deleteDiscount(id: number): Promise<{ success: boolean; message?: string }> {
    if (await this.isDiscountUsed(id)) {
      return { success: false, message: 'Impossible de supprimer cette remise : elle a déjà été affectée à des apprenants ou appliquée sur des factures.' };
    }
    try {
      const res = await academyApi.deleteDiscount(id);
      if (!res.success) return { success: false, message: res.message || 'Failed to delete discount.' };
      await BlobticDataManager.removeItem('academy_discount', id);
      this.notify(['academy_discount']);
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Failed to delete discount.' };
    }
  }

  public async assignDiscountToStudent(studentId: number, discountId: number | null): Promise<{ success: boolean; message?: string }> {
    const students = this.getStudents();
    const student = students.find(s => s.id === studentId);
    if (!student) return { success: false, message: 'Student not found.' };

    const disc = discountId ? this.discounts.find(d => d.id === discountId) : undefined;
    const updatedStudent: Student = {
      ...student,
      discount_id: discountId ?? undefined,
      discount_code: disc?.code,
      discount_name: disc?.name,
    };

    try {
      const res = await academyApi.updateStudentDiscount(studentId, discountId);
      if (!res.success) return { success: false, message: res.message || 'Failed to assign discount to student.' };
      // Keep both camelCase and DSC_* so IndexedDB merge overwrites the previous FK
      // (undefined discount_id does not clear leftover DSC_IDF from the Select DTO).
      await BlobticDataManager.upsertItem('academy_student', {
        ...updatedStudent,
        STD_IDF: student.id,
        DSC_IDF: discountId,
        DSC_CODE: disc?.code ?? null,
        DSC_NAME: disc?.name ?? null,
      });
      const snapKey = 'academy_student';
      if (this._snap[snapKey]) {
        this._snap[snapKey] = (this._snap[snapKey] as Student[]).map(s =>
          s.id === studentId ? updatedStudent : s
        );
      }
      this._notifyListeners();
      return { success: true };
    } catch (err) {
      return { success: false, message: 'Failed to assign discount.' };
    }
  }

  // ─── Quiz Mutations ───────────────────────────────────────────────────────
  public async addQuiz(quiz: Omit<Quiz, 'id'>): Promise<Quiz> {
    const res = await academyApi.createQuiz(quiz);
    if (res && res.success && res.data) {
      const item = Array.isArray(res.data) ? res.data[0] : res.data;
      const mapped = item?.QIZ_IDF ? mapQuiz(item) : { ...quiz, id: Number(item?.id || res.data) } as Quiz;
      await BlobticDataManager.upsertItem('academy_quiz', mapped);
      this.notify();
      return mapped;
    }
    throw new Error('Failed to create quiz.');
  }

  public async updateQuiz(quiz: Quiz): Promise<void> {
    const res = await academyApi.updateQuiz(quiz.id, quiz);
    if (res && res.success) {
      const item = res.data && typeof res.data === 'object' && (res.data as any).QIZ_IDF ? mapQuiz(res.data) : quiz;
      await BlobticDataManager.upsertItem('academy_quiz', item);
      this.notify();
    }
  }

  public async deleteQuiz(quizId: number) {
    await academyApi.deleteQuiz(quizId);
    await BlobticDataManager.removeItem('academy_quiz', quizId);
    this.notify();
  }

  // ─── Academy Parameters ───────────────────────────────────────────────────
  public async refreshAcademyParameters(dosIdf?: number) {
    const prm = await academyApi.getAcademyParameters(dosIdf);
    if (prm !== null) {
      if (dosIdf && dosIdf > 0) {
        this.academyParameters = [...this.academyParameters.filter(p => p.dos_idf !== dosIdf), ...prm];
      } else {
        this.academyParameters = prm;
      }
      this.notify();
    }
    return prm;
  }

  public async saveAcademyParameter(param: Partial<AcademyParameter>) {
    const res = await academyApi.saveAcademyParameter(param);
    if (res && res.success) {
      const returnedId = Number((res.data as any)?.PRM_IDF || (res.data as any)?.id || param.id || res.data);
      const updatedParam: AcademyParameter = {
        id: !isNaN(returnedId) && returnedId > 0 ? returnedId : (param.id || Date.now()),
        prm_uidf: param.prm_uidf, dos_idf: param.dos_idf || 1,
        code: param.code || '', value: param.value || '', lib: param.lib || '', type: param.type || 'S'
      };
      const existingIndex = this.academyParameters.findIndex(p => p.code === param.code && p.dos_idf === (param.dos_idf || 1));
      if (existingIndex >= 0) this.academyParameters[existingIndex] = { ...this.academyParameters[existingIndex], ...updatedParam };
      else this.academyParameters.push(updatedParam);
      this.notify();
    }
    return res;
  }

  public async deleteAcademyParameter(id: number) {
    const res = await academyApi.deleteAcademyParameter(id);
    if (res && res.success) {
      this.academyParameters = this.academyParameters.filter(p => p.id !== id);
      this.notify();
    }
    return res;
  }

  // ─── Invoice / Payment Number Generation ──────────────────────────────────
  public async generateNextInvoiceNumber(dosIdf?: number, dosCode?: string): Promise<string> {
    const t = dosIdf || 1;
    const prefix = this.getAcademyParameterValue('Invoice_Number_Prefix', t, 'INV');
    const agencyLength = parseInt(this.getAcademyParameterValue('Invoice_Number_Agency_Length', t, '4'), 10) || 0;
    const dateFormat = this.getAcademyParameterValue('Invoice_Number_DateFormat', t, 'YYYY');
    const padding = parseInt(this.getAcademyParameterValue('Invoice_Number_Padding', t, '4'), 10) || 4;
    const suffix = this.getAcademyParameterValue('Invoice_Number_Suffix', t, '');
    let currentCounter = parseInt(this.getAcademyParameterValue('Invoice_Number_Next_Counter', t, '1'), 10) || 1;
    const resetPeriod = this.getAcademyParameterValue('Invoice_Number_Reset_Period', t, 'Yearly');
    const lastResetDate = this.getAcademyParameterValue('Invoice_Number_Last_Reset_Date', t, '');
    const order = this.getAcademyParameterValue('Invoice_Number_Order', t, 'PREFIX,AGENCY,DATE,NUMBER,SUFFIX');
    const separator = this.getAcademyParameterValue('Invoice_Number_Separator', t, '-');

    const now = new Date();
    if (shouldResetCounter(resetPeriod, lastResetDate, now)) {
      currentCounter = 1;
      await this.saveAcademyParameter({ dos_idf: t, code: 'Invoice_Number_Last_Reset_Date', value: now.toISOString().split('T')[0], lib: 'Date de la dernière remise à zéro des factures', type: 'S' });
    }

    let targetDosCode = dosCode || '';
    if (!targetDosCode) {
      const params = this.getAcademyParameters(t);
      if (params.length > 0 && params[0].dos_code) targetDosCode = params[0].dos_code;
    }

    const generatedNumber = formatDocumentNumber({ prefix, agencyLength, dateFormat, padding, suffix, order, separator }, currentCounter, now, targetDosCode);
    await this.saveAcademyParameter({ dos_idf: t, code: 'Invoice_Number_Next_Counter', value: String(currentCounter + 1), lib: 'Prochain numéro séquentiel de facture', type: 'N' });
    return generatedNumber;
  }

  public async generateNextPaymentNumber(dosIdf?: number, dosCode?: string): Promise<string> {
    const t = dosIdf || 1;
    const prefix = this.getAcademyParameterValue('Payment_Number_Prefix', t, 'REC');
    const agencyLength = parseInt(this.getAcademyParameterValue('Payment_Number_Agency_Length', t, '4'), 10) || 0;
    const dateFormat = this.getAcademyParameterValue('Payment_Number_DateFormat', t, 'None');
    const padding = parseInt(this.getAcademyParameterValue('Payment_Number_Padding', t, '4'), 10) || 4;
    const suffix = this.getAcademyParameterValue('Payment_Number_Suffix', t, '');
    let currentCounter = parseInt(this.getAcademyParameterValue('Payment_Number_Next_Counter', t, '1'), 10) || 1;
    const resetPeriod = this.getAcademyParameterValue('Payment_Number_Reset_Period', t, 'Never');
    const lastResetDate = this.getAcademyParameterValue('Payment_Number_Last_Reset_Date', t, '');
    const order = this.getAcademyParameterValue('Payment_Number_Order', t, 'PREFIX,NUMBER,SUFFIX');
    const separator = this.getAcademyParameterValue('Payment_Number_Separator', t, '-');

    const now = new Date();
    if (shouldResetCounter(resetPeriod, lastResetDate, now)) {
      currentCounter = 1;
      await this.saveAcademyParameter({ dos_idf: t, code: 'Payment_Number_Last_Reset_Date', value: now.toISOString().split('T')[0], lib: 'Date de la dernière remise à zéro des reçus', type: 'S' });
    }

    let targetDosCode = dosCode || '';
    if (!targetDosCode) {
      const params = this.getAcademyParameters(t);
      if (params.length > 0 && params[0].dos_code) targetDosCode = params[0].dos_code;
    }

    const generatedNumber = formatDocumentNumber({ prefix, agencyLength, dateFormat, padding, suffix, order, separator }, currentCounter, now, targetDosCode);
    await this.saveAcademyParameter({ dos_idf: t, code: 'Payment_Number_Next_Counter', value: String(currentCounter + 1), lib: 'Prochain numéro séquentiel de reçu de paiement', type: 'N' });
    return generatedNumber;
  }

  public async resetInvoiceCounter(newVal: number = 1, dosIdf?: number) {
    const t = dosIdf || 1;
    const todayStr = new Date().toISOString().split('T')[0];
    await this.saveAcademyParameter({ dos_idf: t, code: 'Invoice_Number_Last_Reset_Date', value: todayStr, lib: 'Date de la dernière remise à zéro des factures', type: 'S' });
    return await this.saveAcademyParameter({ dos_idf: t, code: 'Invoice_Number_Next_Counter', value: String(newVal), lib: 'Prochain numéro séquentiel de facture', type: 'N' });
  }

  public async resetPaymentCounter(newVal: number = 1, dosIdf?: number) {
    const t = dosIdf || 1;
    const todayStr = new Date().toISOString().split('T')[0];
    await this.saveAcademyParameter({ dos_idf: t, code: 'Payment_Number_Last_Reset_Date', value: todayStr, lib: 'Date de la dernière remise à zéro des reçus', type: 'S' });
    return await this.saveAcademyParameter({ dos_idf: t, code: 'Payment_Number_Next_Counter', value: String(newVal), lib: 'Prochain numéro séquentiel de reçu de paiement', type: 'N' });
  }

  // ─── Email Templates ────────────────────────────────────────────────────────
  public async getEmailTemplates(useCache = true): Promise<EmailTemplate[]> {
    const data = await academyApi.getEmailTemplates(useCache);
    return data || [];
  }

  public async createEmailTemplate(tpl: Partial<EmailTemplate>) {
    const res = await academyApi.createEmailTemplate(tpl);
    if (res.success) {
      delete this._snap['academy_email_template'];
      this.notify(['academy_email_template']);
    }
    return res;
  }

  public async updateEmailTemplate(id: number, tpl: Partial<EmailTemplate>) {
    const res = await academyApi.updateEmailTemplate(id, tpl);
    if (res.success) {
      delete this._snap['academy_email_template'];
      this.notify(['academy_email_template']);
    }
    return res;
  }

  public async deleteEmailTemplate(id: number) {
    const res = await academyApi.deleteEmailTemplate(id);
    if (res.success) {
      delete this._snap['academy_email_template'];
      this.notify(['academy_email_template']);
    }
    return res;
  }

  // ─── Subscription & Quotas ────────────────────────────────────────────────
  public async getSubscriptionPlans(forceRefresh: boolean = false): Promise<AcademySubscriptionPlan[]> {
    if (!forceRefresh && this._snap['academy_subscription_plan']) {
      return this._snap['academy_subscription_plan'] as AcademySubscriptionPlan[];
    }
    const plans = await academyApi.getSubscriptionPlans();
    this._snap['academy_subscription_plan'] = plans;
    return plans;
  }

  public async getSubscriptionUsage(forceRefresh: boolean = false): Promise<SubscriptionUsage> {
    if (!forceRefresh && this._snap['academy_subscription_usage']) {
      return this._snap['academy_subscription_usage'] as SubscriptionUsage;
    }
    const usage = await academyApi.getSubscriptionUsage();
    this._snap['academy_subscription_usage'] = usage;
    return usage;
  }

  public async getSubscriptionInvoices(forceRefresh: boolean = false): Promise<AcademySubscriptionInvoice[]> {
    if (!forceRefresh && this._snap['academy_subscription_invoice']) {
      return this._snap['academy_subscription_invoice'] as AcademySubscriptionInvoice[];
    }
    const invoices = await academyApi.getSubscriptionInvoices();
    this._snap['academy_subscription_invoice'] = invoices;
    return invoices;
  }

  public async upgradeSubscription(params: { planId: number; billingCycle: 'monthly' | 'yearly'; paymentMethod?: string; transactionId?: string; dosId?: number }) {
    const res = await academyApi.upgradeSubscription(params);
    this.invalidateSubscription();
    return res;
  }

  public invalidateSubscription() {
    delete this._snap['academy_subscription_usage'];
    delete this._snap['academy_subscription_invoice'];
    delete this._snap['academy_subscription'];
    delete this._snap['academy_subscription_plan'];
    this._notifyListeners();
  }

  // ─── Cache Reset ──────────────────────────────────────────────────────────
  public async resetCache() {
    await BlobticDataManager.dropAllStores();
    this._snap = {};
    this._loading = {};
    await this.refreshDashboardStats();
    this._notifyListeners();
  }
}

export const academyStore = new AcademyStore();

export function generateTimeSlots(startTime: string = '08:00', endTime: string = '17:30', durationMinutes: number = 90): { start: string; end: string; label: string }[] {
  const slots: { start: string; end: string; label: string }[] = [];
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let currentMins = (isNaN(startH) ? 8 : startH) * 60 + (isNaN(startM) ? 0 : startM);
  const maxMins = (isNaN(endH) ? 17 : endH) * 60 + (isNaN(endM) ? 30 : endM);
  const dur = durationMinutes > 0 ? durationMinutes : 90;

  const formatTime = (totalMins: number) => `${String(Math.floor(totalMins / 60)).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`;
  const formatLabel = (totalMins: number) => {
    const h = Math.floor(totalMins / 60);
    return `${h % 12 === 0 ? 12 : h % 12}:${String(totalMins % 60).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  while (currentMins + dur <= maxMins) {
    const nextMins = currentMins + dur;
    slots.push({ start: formatTime(currentMins), end: formatTime(nextMins), label: `${formatLabel(currentMins)} - ${formatLabel(nextMins)}` });
    currentMins = nextMins;
  }

  return slots.length > 0 ? slots : [
    { start: '08:00', end: '09:30', label: '8:00 AM - 9:30 AM' },
    { start: '09:30', end: '11:00', label: '9:30 AM - 11:00 AM' },
    { start: '11:00', end: '12:30', label: '11:00 AM - 12:30 PM' },
    { start: '13:00', end: '14:30', label: '1:00 PM - 2:30 PM' },
    { start: '14:30', end: '16:00', label: '2:30 PM - 4:00 PM' },
    { start: '16:00', end: '17:30', label: '4:00 PM - 5:30 PM' },
  ];
}

export function getOrderedDaysOfWeek(firstDayOfWeek: number = 0): { val: number; key: string; fr: string }[] {
  const ALL_DAYS = [
    { val: 0, key: 'days.sun', fr: 'Dimanche' },
    { val: 1, key: 'days.mon', fr: 'Lundi' },
    { val: 2, key: 'days.tue', fr: 'Mardi' },
    { val: 3, key: 'days.wed', fr: 'Mercredi' },
    { val: 4, key: 'days.thu', fr: 'Jeudi' },
    { val: 5, key: 'days.fri', fr: 'Vendredi' },
    { val: 6, key: 'days.sat', fr: 'Samedi' },
  ];
  const startIndex = ALL_DAYS.findIndex(d => d.val === firstDayOfWeek);
  if (startIndex === -1) return ALL_DAYS;
  return [...ALL_DAYS.slice(startIndex), ...ALL_DAYS.slice(0, startIndex)];
}
